import {
  ChevronRightIcon,
  MoonIcon,
  SearchIcon,
  StarIcon,
} from '@chakra-ui/icons'
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link as RouterLink } from 'react-router-dom'
import { LazyImage } from '../components/LazyImage'
import { POSTS } from '../data/posts'
import { domain, pageTitle } from '../global'
import { useTranslation } from 'react-i18next'

const HomePage: React.FC = () => {
  const { t } = useTranslation()
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const heroBgColor = useColorModeValue('purple.100', 'purple.900')
  const cardBgColor = useColorModeValue('white', 'gray.800')
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const headingColor = useColorModeValue('purple.600', 'purple.300')
  const blogPosts = POSTS.slice(0, 3)

  return (
    <>
      <Helmet>
        <title>{pageTitle(t('home.title'))}</title>
        <meta
          name="description"
          content={t('home.description', { domain: domain })}
        />
        <link
          rel="preload"
          href="/assets/hero2.webp"
          as="image"
          type="image/webp"
        />
      </Helmet>

      <Box bg={bgColor} minH="100vh">
        {/* Hero Section */}
        <Box py={{ base: 10, md: 20 }}>
          <Container maxW="container.xl">
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              align="center"
              justify="space-between"
            >
              <VStack
                align={{ base: 'center', lg: 'start' }}
                spacing={8}
                flex={1}
                mr={{ base: 0, lg: 8 }}
                mb={{ base: 8, lg: 0 }}
              >
                <Heading
                  as="h1"
                  size="2xl"
                  textAlign={{ base: 'center', lg: 'left' }}
                  color={headingColor}
                >
                  {t('home.hero.title')}
                </Heading>
                <Text
                  fontSize="xl"
                  textAlign={{ base: 'center', lg: 'left' }}
                  color={textColor}
                >
                  {t('home.hero.description')}
                </Text>
                <HStack
                  spacing={4}
                  wrap="wrap"
                  justify={{ base: 'center', lg: 'flex-start' }}
                >
                  <Button
                    as={RouterLink}
                    to="/vyklad"
                    colorScheme="purple"
                    size="lg"
                    rightIcon={<ChevronRightIcon />}
                  >
                    {t('home.cta.buttons.interpret')}
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/snar"
                    colorScheme="purple"
                    variant="outline"
                    size="lg"
                    rightIcon={<ChevronRightIcon />}
                  >
                    {t('home.cta.buttons.browse')}
                  </Button>
                </HStack>
              </VStack>
              <Box flex={1} mt={{ base: 8, lg: 0 }}>
                <LazyImage
                  src="/assets/hero2"
                  alt="Hero image"
                  priority={true}
                />
              </Box>
            </Flex>
          </Container>
        </Box>

        {/* Features Section */}
        <Box
          bg={useColorModeValue('white', 'gray.800')}
          py={{ base: 10, md: 20 }}
        >
          <Container maxW="container.xl">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
              <FeatureBox
                icon={MoonIcon}
                title={t('home.features.dreamBook.title')}
                description={t('home.features.dreamBook.description')}
              />
              <FeatureBox
                icon={SearchIcon}
                title={t('home.features.interpretation.title')}
                description={t('home.features.interpretation.description')}
              />
              <FeatureBox
                icon={StarIcon}
                title={t('home.features.why.title')}
                description={t('home.features.why.description')}
              />
            </SimpleGrid>
          </Container>
        </Box>

        {/* Stats Section */}
        <Container maxW="container.xl" py={{ base: 10, md: 20 }}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            <Stat>
              <StatLabel>{t('home.stats.symbols.label')}</StatLabel>
              <StatNumber>{t('home.stats.symbols.value')}</StatNumber>
              <StatHelpText>{t('home.stats.symbols.help')}</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>{t('home.stats.interpretations.label')}</StatLabel>
              <StatNumber>{t('home.stats.interpretations.value')}</StatNumber>
              <StatHelpText>
                {t('home.stats.interpretations.help')}
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>{t('home.stats.daily.label')}</StatLabel>
              <StatNumber>{t('home.stats.daily.value')}</StatNumber>
              <StatHelpText>{t('home.stats.daily.help')}</StatHelpText>
            </Stat>
          </SimpleGrid>
        </Container>

        {/* About Section */}
        <Box
          bg={useColorModeValue('white', 'gray.800')}
          py={{ base: 10, md: 20 }}
        >
          <Container maxW="container.xl">
            <VStack spacing={8} align="stretch">
              <Heading
                as="h2"
                size="xl"
                textAlign="center"
                color={headingColor}
              >
                {t('home.about.title')}
              </Heading>
              <Text fontSize="lg" color={textColor}>
                {t('home.about.description1', { domain })}
              </Text>
              <Text fontSize="lg" color={textColor}>
                {t('home.about.description2')}
              </Text>
            </VStack>
          </Container>
        </Box>

        <Container maxW="container.xl" py={{ base: 10, md: 20 }}>
          <VStack spacing={8} align="stretch">
            <Heading as="h2" size="xl" textAlign="center" color={headingColor}>
              {t('home.blog.title')}
            </Heading>
            <Text fontSize="xl" textAlign="center" color={textColor}>
              {t('home.blog.description')}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {blogPosts.map((post) => (
                <Box
                  key={post.id}
                  bg={cardBgColor}
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="md"
                >
                  <LazyImage
                    src={post.image}
                    alt={post.title}
                    width="100%"
                    height={200}
                    objectFit="cover"
                  />
                  <VStack p={5} align="start" spacing={3}>
                    <Heading as="h3" size="md" color={headingColor}>
                      {post.title}
                    </Heading>
                    <Text color={textColor} noOfLines={3}>
                      {post.excerpt}
                    </Text>
                    <HStack justify="flex-end" w="100%">
                      <Button
                        as={RouterLink}
                        to={`/blog/${post.id}`}
                        rightIcon={<ChevronRightIcon />}
                        colorScheme="purple"
                        variant="outline"
                        size="sm"
                      >
                        {t('home.blog.readMore')}
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
            <Flex justify="center" mt={6}>
              <Button
                as={RouterLink}
                to="/blog"
                colorScheme="purple"
                variant="outline"
                rightIcon={<ChevronRightIcon />}
              >
                {t('home.blog.viewAll')}
              </Button>
            </Flex>
          </VStack>
        </Container>

        {/* CTA Section */}
        <Box
          bg={useColorModeValue('white', 'gray.800')}
          py={{ base: 10, md: 20 }}
        >
          <Container maxW="container.xl">
            <VStack spacing={8} textAlign="center">
              <Heading as="h2" size="xl" color={headingColor}>
                {t('home.cta.title')}
              </Heading>
              <Text fontSize="xl" color={textColor} maxW="2xl">
                {t('home.cta.description')}
              </Text>
              <HStack spacing={6} wrap="wrap" justify="center">
                <Button
                  as={RouterLink}
                  to="/vyklad"
                  colorScheme="purple"
                  size="lg"
                  rightIcon={<ChevronRightIcon />}
                >
                  {t('home.cta.buttons.interpret')}
                </Button>
                <Button
                  as={RouterLink}
                  to="/snar"
                  colorScheme="purple"
                  variant="outline"
                  size="lg"
                  rightIcon={<ChevronRightIcon />}
                >
                  {t('home.cta.buttons.browse')}
                </Button>
              </HStack>
              <Text fontSize="sm" color={textColor}>
                {t('home.cta.free')}
              </Text>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  )
}

interface FeatureBoxProps {
  icon: React.ElementType
  title: string
  description: string
}

const FeatureBox: React.FC<FeatureBoxProps> = ({
  icon,
  title,
  description,
}) => {
  return (
    <VStack align="start" spacing={4}>
      <Icon as={icon} w={10} h={10} color="purple.500" />
      <Heading as="h3" size="md">
        {title}
      </Heading>
      <Text>{description}</Text>
    </VStack>
  )
}

export default HomePage
